import React from 'react';
import { IProps as IFixedContentBlockProps } from './FixedContentBlock.interface';
import { IProps as ICardProps } from '@/components/Card/Card.interface';
import { FixedContentBlock } from './FixedContentBlock.component'; // Named import
import TranslationComponent from '@/utils/TranslationComponent/TranslationComponent';
import * as translation from '@/utils/TranslationComponent/translation.utilities';

interface IInputLink {
    text: string | null;
    target: string | null;
    href: string;
    title: string | null;
    ariaLabel: string | null;
}

interface IInputButton {
    link: IInputLink;
    buttonText: string | null;
    btnStyle: string | null; // Consider using a literal type if there's a fixed set of styles e.g. 'primary' | 'secondary'
    eventName: string | null;
    icon: string | null;
    iconDirection: string | null;
}

interface IContactInfoInputItem {
    icon: string;
    text: string | null;
    link: IInputLink | null;
}

interface IContactInputInfo {
    parentClass: string | null;
    items: IContactInfoInputItem[];
    anchorId: string | null;
}

interface IInputItem {
    parentClass: string | null;
    image: any | null; // Assuming IImage is a pre-existing interface for image objects
    heading: string;
    summary: string;
    button: IInputButton | null;
    cardBackgroundTheme: string; // Consider using a literal type e.g. 'white' | 'gray'
    categories: string[] | null;
    date: string;
    contactInfo: IContactInputInfo | null;
}

export interface IFixedContentBlockInputProps {
    parentClass?: string | null;
    heading?: string;
    anchorId?: string | null;
    items?: IInputItem[];
    backgroundTheme?: string; // Consider using a literal type e.g. 'violet' | 'blue'
    columns?: 'one' | 'two' | 'three' | 'four'; // Assuming these are the possible column values
    button?: IInputButton | null;
    [key: string]: any;
}



/**
 * Maps input props (e.g., from a CMS) to the props expected by the FixedContentBlock component.
 * @param input - The input props.
 * @returns The mapped props for the FixedContentBlock component.
 */
const mapInputToProps = (input: IFixedContentBlockInputProps): IFixedContentBlockProps => {
    const {
        parentClass,
        heading,
        anchorId,
        items,
        backgroundTheme,
        columns,
        button,
        ...rest
    } = input;

    return {
        title: heading,
        cta: button ? translation.mapAnchorProps(button) : undefined,
        cards: items?.map((item: any) => translation.mapCardProps(item)) as ICardProps[],
        backgroundColor: backgroundTheme || 'white',
        ...rest,
    } as IFixedContentBlockProps;
};

/**
 * The translation component for FixedContentBlock.
 * It uses TranslationComponent to handle the mapping between input props and component props.
 */
const FixedContentBlockTranslation: React.FC<IFixedContentBlockInputProps> = (inputProps) => {
    return (
        <TranslationComponent
            inputProps={inputProps}
            TargetComponent={FixedContentBlock}
            mapProps={mapInputToProps}
        />
    );
};

export default FixedContentBlockTranslation;
